/**
 * Muayene detay payload codec'i — SAF modül (React'sız).
 *
 * encode: form'daki weld sonucu -> DB'ye JSON.stringify ile yazılan payload.
 * (decode karşılığı Adım 3'te eklenecek; QaqcPipeInspectionEdit.tsx'teki
 * ters-parse buraya taşınacak. İkisi aynı dosyada durur ki şema tek yerde
 * değişsin.)
 *
 * Davranış PipeInspectionForm.tsx'in eski :108-263 bloğundan BİREBİR taşındı.
 * Bilinçli olarak korunan tuhaflık: VC kontrolü `String(type).toUpperCase()`
 * kullanırken diğer tipler katı eşitlik kullanır.
 */
import dayjs from 'dayjs';
import { InspectionDetailPayload } from './types';

export const num = (v: any) => (v === '' || v === undefined ? null : (Number(v) as number));
export const bool = (v: any) => (v === undefined ? false : Boolean(v));
export const strOrNull = (v: any) => (v === '' || v === undefined ? null : String(v));
export const radioOrNull = (v: any) => (v === undefined ? null : v);

/**
 * Tek bir weld'in form sonucunu (watchedResults[id]) normalize edilmiş
 * payload'a çevirir.
 */
export function encodeWeldResult(inspectionType: string | undefined, r: any): InspectionDetailPayload {
  const base: any = { result: r?.result || 'PASS' };
  if (inspectionType === 'BC') {
    const bcSrc = (r as any)?.bc || {};
    base.bc = {
      bevel_angle: num(bcSrc.bevel_angle),
      root_face_mm: num(bcSrc.root_face_mm),
      root_gap_mm: num(bcSrc.root_gap_mm),
      hi_lo_mm: num(bcSrc?.fitup?.hi_lo_mm),
      ovality_ok: bool(bcSrc?.fitup?.ovality_ok),
      prep_method: radioOrNull(bcSrc.prep_method),
      cleaning_ok: bool(bcSrc.cleaning_ok),
      cracks: bool(bcSrc?.surface_ok?.cracks),
      laminations: bool(bcSrc?.surface_ok?.laminations),
      rust: bool(bcSrc?.surface_ok?.rust),
      paint_oil: bool(bcSrc?.surface_ok?.paint_oil),
      size_spacing: bool(bcSrc?.tack_welds_ok?.size_spacing),
      defects_free: bool(bcSrc?.tack_welds_ok?.defects_free),
      electrode_batch_ok: bool(bcSrc?.consumables_env?.electrode_batch_ok),
      preheat_ok: bool(bcSrc?.consumables_env?.preheat_ok),
      preheat_temp: num(bcSrc?.consumables_env?.preheat_temp),
      ambient_ok: bool(bcSrc?.consumables_env?.ambient_ok),
      heat_no_1: strOrNull(bcSrc.heat_no_1),
      heat_no_2: strOrNull(bcSrc.heat_no_2),
      remarks: strOrNull(bcSrc.remarks),
    };
  }
  if (String(inspectionType).toUpperCase() === 'VC') {
    const vcSrc = (r as any)?.vc || {};
    base.vc = {
      // External profile and dimensions
      weld_size_ok: bool(vcSrc?.external_profile_dimensions?.weld_size_ok),
      reinforcement_ok: bool(vcSrc?.external_profile_dimensions?.reinforcement_ok),
      contour_ok: bool(vcSrc?.external_profile_dimensions?.contour_ok),
      undersize_oversize_ok: bool(vcSrc?.external_profile_dimensions?.undersize_oversize_ok),
      // Surface discontinuities
      no_cracks: bool(vcSrc?.surface_discontinuities?.no_cracks),
      no_porosity: bool(vcSrc?.surface_discontinuities?.no_porosity),
      undercut_ok: bool(vcSrc?.surface_discontinuities?.undercut_ok),
      no_overlap: bool(vcSrc?.surface_discontinuities?.no_overlap),
      fusion_fill_ok: bool(vcSrc?.surface_discontinuities?.fusion_fill_ok),
      // Root and penetration
      root_condition_ok: bool(vcSrc?.root_penetration?.root_condition_ok),
      penetration_ok: bool(vcSrc?.root_penetration?.penetration_ok),
      root_reinforcement_ok: bool(vcSrc?.root_penetration?.root_reinforcement_ok),
      // Cleanliness and contaminants
      slag_spatter_removed: bool(vcSrc?.cleanliness_contaminants?.slag_spatter_removed),
      no_foreign_inclusions: bool(vcSrc?.cleanliness_contaminants?.no_foreign_inclusions),
      toes_adjacent_clean: bool(vcSrc?.cleanliness_contaminants?.toes_adjacent_clean),
      // Thermal effects (HAZ)
      discoloration_ok: bool(vcSrc?.haz_thermal_effects?.discoloration_ok),
      no_haz_cracking: bool(vcSrc?.haz_thermal_effects?.no_haz_cracking),
      coating_damage_checked: bool(vcSrc?.haz_thermal_effects?.coating_damage_checked),
      // Distortion and alignment
      misalignment_ok: bool(vcSrc?.distortion_alignment?.misalignment_ok),
      no_twist_bow: bool(vcSrc?.distortion_alignment?.no_twist_bow),
      dimensional_checks_ok: bool(vcSrc?.distortion_alignment?.dimensional_checks_ok),
      // Finish and markings
      smooth_transition_tack_ok: bool(vcSrc?.finish_markings?.smooth_transition_tack_ok),
      spatter_ok: bool(vcSrc?.finish_markings?.spatter_ok),
      // Remarks
      remarks: strOrNull(vcSrc?.remarks),
      // Welders (joined strings only)
      flw_welder: strOrNull(vcSrc?.flw_welder),
      fw_welder: strOrNull(vcSrc?.fw_welder),
    };
  }
  if (inspectionType === 'PT') {
    const ptSrc = (r as any)?.pt || {};
    base.pt = {
      surface_cleaned: bool(ptSrc?.preparation?.surface_cleaned ?? ptSrc?.surface_cleaned),
      penetrant_type: radioOrNull(ptSrc?.application?.penetrant_type ?? ptSrc?.penetrant_type),
      dwell_time_min: num(ptSrc?.application?.dwell_time_min ?? ptSrc?.dwell_time_min),
      surface_cleaned_no_residue: bool(ptSrc?.application?.surface_cleaned_no_residue ?? ptSrc?.surface_cleaned_no_residue),
      type: strOrNull(ptSrc?.developer?.type ?? ptSrc?.type),
      development_time_min: num(ptSrc?.developer?.development_time_min ?? ptSrc?.development_time_min),
      no_cracks: bool(ptSrc?.indications?.no_cracks ?? ptSrc?.no_cracks),
      proper_fusion: bool(ptSrc?.indications?.proper_fusion ?? ptSrc?.proper_fusion),
      no_porosity: bool(ptSrc?.indications?.no_porosity ?? ptSrc?.no_porosity),
      no_laps: bool(ptSrc?.indications?.no_laps ?? ptSrc?.no_laps),
      post_cleaning: bool(ptSrc?.finish?.post_cleaning ?? ptSrc?.post_cleaning),
      remarks: strOrNull(ptSrc?.remarks),
    };
  }
  if (inspectionType === 'PWHT') {
    const n = (r as any)?.hardness != null && (r as any)?.hardness !== '' ? Number((r as any)?.hardness) : null;
    base.hardness = n;
  }
  if (inspectionType === 'MT') {
    const mt = (r as any)?.mt || {};
    base.mt = {
      clean: bool(mt?.preparation?.clean ?? mt?.clean),
      coating_removed: bool(mt?.preparation?.coating_removed ?? mt?.coating_removed),
      method: radioOrNull(mt?.method),
      current_type: radioOrNull(mt?.current?.type ?? mt?.current_type),
      current_amperes: strOrNull(mt?.current?.amperes ?? mt?.current_amperes),
      lift_test: radioOrNull(mt?.lift_test),
      flux_density: strOrNull(mt?.field_strength?.flux_density ?? mt?.flux_density),
      field_units: radioOrNull(mt?.field_strength?.units ?? mt?.field_units),
      particle_type: radioOrNull(mt?.particle?.type ?? mt?.particle_type),
      particle_brand_batch: strOrNull(mt?.particle?.brand_batch ?? mt?.particle_brand_batch),
      particle_applied: (() => {
        const t = radioOrNull(mt?.particle?.type ?? mt?.particle_type);
        return t && t !== 'na' ? true : false;
      })(),
      lighting: strOrNull(mt?.lighting),
      lighting_unit: (() => {
        const t = radioOrNull(mt?.particle?.type ?? mt?.particle_type);
        if (!t || t === 'na') return null;
        return t === 'wet_fluorescent' ? 'uv' : 'lux';
      })(),
      development_time_min: num(mt?.development_time_min),
      no_cracks: bool(mt?.indications?.no_cracks ?? mt?.no_cracks),
      proper_fusion: bool(mt?.indications?.proper_fusion ?? mt?.proper_fusion),
      no_porosity: bool(mt?.indications?.no_porosity ?? mt?.no_porosity),
      no_laps: bool(mt?.indications?.no_laps ?? mt?.no_laps),
      post_cleaning: bool(mt?.finish?.post_cleaning ?? mt?.post_cleaning),
      remarks: strOrNull(mt?.remarks),
    };
  }
  if (inspectionType === 'UT') {
    const ut = (r as any)?.ut || {};
    base.ut = {
      equipment: strOrNull(ut?.equipment),
      probe_type: radioOrNull(ut?.probe_type),
      frequency_mhz: strOrNull(ut?.frequency_mhz),
      probe_angle: strOrNull(ut?.probe_angle),
      calibration_block: radioOrNull(ut?.calibration_block),
      couplant_type: radioOrNull(ut?.couplant_type),
      scan_method: radioOrNull(ut?.scan_method),
      scan_coverage: radioOrNull(ut?.scan_coverage),
      coverage_percent: strOrNull(ut?.coverage_percent),
      sensitivity_mode: radioOrNull(ut?.sensitivity_mode),
      sensitivity_db: strOrNull(ut?.sensitivity_db),
      range_value: strOrNull(ut?.range_value),
      range_unit: radioOrNull(ut?.range_unit),
      sweep_value: strOrNull(ut?.sweep_value),
      sweep_unit: radioOrNull(ut?.sweep_unit),
      gate_start: strOrNull(ut?.gate_start),
      gate_width: strOrNull(ut?.gate_width),
      gate_threshold_percent: strOrNull(ut?.gate_threshold_percent),
      no_cracks: bool(ut?.indications?.no_cracks ?? ut?.no_cracks),
      proper_fusion: bool(ut?.indications?.proper_fusion ?? ut?.proper_fusion),
      no_porosity: bool(ut?.indications?.no_porosity ?? ut?.no_porosity),
      no_laps: bool(ut?.indications?.no_laps ?? ut?.no_laps),
      remarks: strOrNull(ut?.remarks),
    };
  }
  return base as InspectionDetailPayload;
}

/**
 * DB'deki payload'ı (JSON.parse edilmiş) form ağacı şekline çevirir.
 *
 * Davranış QaqcPipeInspectionEdit.tsx'in eski :106-325 bloğundan BİREBİR
 * taşındı. Dallanma inspectionType'a değil payload ANAHTARINA göredir
 * (payload.bc varsa bc çözülür vb.) — eski kayıtlarla uyum için korunur.
 * VC tarihleri dayjs nesnesine çevrilir (DatePicker sözleşmesi).
 */
export function decodeDetailPayload(payload: any): Record<string, any> {
  const toBool = (v: any) => (typeof v === 'string' ? v.toLowerCase() === 'true' : Boolean(v));
  const toNum = (v: any) => {
    if (v === '' || v === null || v === undefined) return undefined;
    const n = Number(v); return Number.isFinite(n) ? n : undefined;
  };
  const out: any = {};
  if (payload.result) out.result = String(payload.result);
  if (payload.hardness != null) out.hardness = toNum(payload.hardness);
  if (payload.bc) {
    const bcSrc = payload.bc || {};
    const fitup = { hi_lo_mm: toNum(bcSrc.hi_lo_mm), ovality_ok: bcSrc.ovality_ok !== undefined ? toBool(bcSrc.ovality_ok) : undefined };
    const surface_ok = { cracks: bcSrc.cracks !== undefined ? toBool(bcSrc.cracks) : undefined, laminations: bcSrc.laminations !== undefined ? toBool(bcSrc.laminations) : undefined, rust: bcSrc.rust !== undefined ? toBool(bcSrc.rust) : undefined, paint_oil: bcSrc.paint_oil !== undefined ? toBool(bcSrc.paint_oil) : undefined };
    const tack_welds_ok = { size_spacing: bcSrc.size_spacing !== undefined ? toBool(bcSrc.size_spacing) : undefined, defects_free: bcSrc.defects_free !== undefined ? toBool(bcSrc.defects_free) : undefined };
    const consumables_env = { electrode_batch_ok: bcSrc.electrode_batch_ok !== undefined ? toBool(bcSrc.electrode_batch_ok) : undefined, preheat_ok: bcSrc.preheat_ok !== undefined ? toBool(bcSrc.preheat_ok) : undefined, preheat_temp: toNum(bcSrc.preheat_temp), ambient_ok: bcSrc.ambient_ok !== undefined ? toBool(bcSrc.ambient_ok) : undefined };
    out.bc = {
      bevel_angle: toNum(bcSrc.bevel_angle),
      root_face_mm: toNum(bcSrc.root_face_mm),
      root_gap_mm: toNum(bcSrc.root_gap_mm),
      heat_no_1: bcSrc.heat_no_1 ?? undefined,
      heat_no_2: bcSrc.heat_no_2 ?? undefined,
      fitup,
      prep_method: bcSrc.prep_method ?? undefined,
      cleaning_ok: bcSrc.cleaning_ok !== undefined ? toBool(bcSrc.cleaning_ok) : undefined,
      surface_ok,
      tack_welds_ok,
      consumables_env,
      remarks: bcSrc.remarks ?? undefined,
    };
  }
  if (payload.ut) {
    const utSrc = payload.ut || {} as any;
    const toStr = (v: any) => (v === '' || v == null ? undefined : String(v));
    const toBoolUt = (v: any) => (v === undefined ? undefined : (typeof v === 'string' ? v.toLowerCase() === 'true' : Boolean(v)));
    const outUt: any = {
      equipment: toStr(utSrc.equipment),
      probe_type: toStr(utSrc.probe_type),
      frequency_mhz: toStr(utSrc.frequency_mhz),
      probe_angle: toStr(utSrc.probe_angle),
      calibration_block: toStr(utSrc.calibration_block),
      couplant_type: toStr(utSrc.couplant_type),
      scan_method: toStr(utSrc.scan_method),
      scan_coverage: toStr(utSrc.scan_coverage),
      coverage_percent: toStr(utSrc.coverage_percent),
      sensitivity_mode: toStr(utSrc.sensitivity_mode),
      sensitivity_db: toStr(utSrc.sensitivity_db),
      range_value: toStr(utSrc.range_value),
      range_unit: toStr(utSrc.range_unit),
      sweep_value: toStr(utSrc.sweep_value),
      sweep_unit: toStr(utSrc.sweep_unit),
      gate_start: toStr(utSrc.gate_start),
      gate_width: toStr(utSrc.gate_width),
      gate_threshold_percent: toStr(utSrc.gate_threshold_percent),
      indications: {
        no_cracks: toBoolUt(utSrc.no_cracks ?? utSrc?.indications?.no_cracks),
        proper_fusion: toBoolUt(utSrc.proper_fusion ?? utSrc?.indications?.proper_fusion),
        no_porosity: toBoolUt(utSrc.no_porosity ?? utSrc?.indications?.no_porosity),
        no_laps: toBoolUt(utSrc.no_laps ?? utSrc?.indications?.no_laps),
      },
      remarks: toStr(utSrc.remarks),
    };
    out.ut = outUt;
  }
  if (payload.mt) {
    const mtSrc = payload.mt || {};
    const preparation = {
      clean: mtSrc.clean !== undefined ? toBool(mtSrc.clean) : undefined,
      coating_removed: mtSrc.coating_removed !== undefined ? toBool(mtSrc.coating_removed) : undefined,
    };
    const method = mtSrc.method ?? undefined;
    const current = {
      type: mtSrc.current_type ?? undefined,
      amperes: mtSrc.current_amperes ?? undefined,
    } as any;
    const lift_test = mtSrc.lift_test ?? undefined;
    const field_strength = {
      flux_density: mtSrc.flux_density ?? undefined,
      units: mtSrc.field_units ?? undefined,
    } as any;
    const particle = {
      type: mtSrc.particle_type ?? undefined,
      brand_batch: mtSrc.particle_brand_batch ?? undefined,
      particle_applied: mtSrc.particle_applied !== undefined ? toBool(mtSrc.particle_applied) : undefined,
    } as any;
    const lighting = mtSrc.lighting ?? undefined;
    const lighting_unit = (() => {
      const t = mtSrc.particle_type ?? undefined;
      if (!t || t === 'na') return undefined;
      return t === 'wet_fluorescent' ? 'uv' : 'lux';
    })();
    const development_time_min = toNum(mtSrc.development_time_min);
    const indications = {
      no_cracks: mtSrc.no_cracks !== undefined ? toBool(mtSrc.no_cracks) : undefined,
      proper_fusion: mtSrc.proper_fusion !== undefined ? toBool(mtSrc.proper_fusion) : undefined,
      no_porosity: mtSrc.no_porosity !== undefined ? toBool(mtSrc.no_porosity) : undefined,
      no_laps: mtSrc.no_laps !== undefined ? toBool(mtSrc.no_laps) : undefined,
    };
    const finish = {
      post_cleaning: mtSrc.post_cleaning !== undefined ? toBool(mtSrc.post_cleaning) : undefined,
    };
    out.mt = {
      preparation,
      method,
      current,
      lift_test,
      field_strength,
      particle,
      lighting,
      lighting_unit,
      development_time_min,
      indications,
      finish,
      remarks: mtSrc.remarks ?? undefined,
    };
  }
  if (payload.pt) {
    const ptSrc = payload.pt || {};
    const preparation = {
      surface_cleaned: ptSrc.surface_cleaned !== undefined ? toBool(ptSrc.surface_cleaned) : undefined,
    };
    const application = {
      penetrant_type: ptSrc.penetrant_type ?? undefined,
      dwell_time_min: toNum(ptSrc.dwell_time_min),
      surface_cleaned_no_residue: ptSrc.surface_cleaned_no_residue !== undefined ? toBool(ptSrc.surface_cleaned_no_residue) : undefined,
    };
    const developer = {
      type: ptSrc.type ?? undefined,
      development_time_min: toNum(ptSrc.development_time_min),
    };
    const indications = {
      no_cracks: ptSrc.no_cracks !== undefined ? toBool(ptSrc.no_cracks) : undefined,
      proper_fusion: ptSrc.proper_fusion !== undefined ? toBool(ptSrc.proper_fusion) : undefined,
      no_porosity: ptSrc.no_porosity !== undefined ? toBool(ptSrc.no_porosity) : undefined,
      no_laps: ptSrc.no_laps !== undefined ? toBool(ptSrc.no_laps) : undefined,
    };
    const finish = {
      post_cleaning: ptSrc.post_cleaning !== undefined ? toBool(ptSrc.post_cleaning) : undefined,
    };
    out.pt = {
      preparation,
      application,
      developer,
      indications,
      finish,
      remarks: ptSrc.remarks ?? undefined,
    };
  }
  if (payload.vc) {
    const vcSrc = payload.vc || {};
    const external_profile_dimensions = {
      weld_size_ok: vcSrc.weld_size_ok !== undefined ? toBool(vcSrc.weld_size_ok) : undefined,
      reinforcement_ok: vcSrc.reinforcement_ok !== undefined ? toBool(vcSrc.reinforcement_ok) : undefined,
      contour_ok: vcSrc.contour_ok !== undefined ? toBool(vcSrc.contour_ok) : undefined,
      undersize_oversize_ok: vcSrc.undersize_oversize_ok !== undefined ? toBool(vcSrc.undersize_oversize_ok) : undefined,
    };
    const surface_discontinuities = {
      no_cracks: vcSrc.no_cracks !== undefined ? toBool(vcSrc.no_cracks) : undefined,
      no_porosity: vcSrc.no_porosity !== undefined ? toBool(vcSrc.no_porosity) : undefined,
      undercut_ok: vcSrc.undercut_ok !== undefined ? toBool(vcSrc.undercut_ok) : undefined,
      no_overlap: vcSrc.no_overlap !== undefined ? toBool(vcSrc.no_overlap) : undefined,
      fusion_fill_ok: vcSrc.fusion_fill_ok !== undefined ? toBool(vcSrc.fusion_fill_ok) : undefined,
    };
    const root_penetration = {
      root_condition_ok: vcSrc.root_condition_ok !== undefined ? toBool(vcSrc.root_condition_ok) : undefined,
      penetration_ok: vcSrc.penetration_ok !== undefined ? toBool(vcSrc.penetration_ok) : undefined,
      root_reinforcement_ok: vcSrc.root_reinforcement_ok !== undefined ? toBool(vcSrc.root_reinforcement_ok) : undefined,
    };
    const cleanliness_contaminants = {
      slag_spatter_removed: vcSrc.slag_spatter_removed !== undefined ? toBool(vcSrc.slag_spatter_removed) : undefined,
      no_foreign_inclusions: vcSrc.no_foreign_inclusions !== undefined ? toBool(vcSrc.no_foreign_inclusions) : undefined,
      toes_adjacent_clean: vcSrc.toes_adjacent_clean !== undefined ? toBool(vcSrc.toes_adjacent_clean) : undefined,
    };
    const haz_thermal_effects = {
      discoloration_ok: vcSrc.discoloration_ok !== undefined ? toBool(vcSrc.discoloration_ok) : undefined,
      no_haz_cracking: vcSrc.no_haz_cracking !== undefined ? toBool(vcSrc.no_haz_cracking) : undefined,
      coating_damage_checked: vcSrc.coating_damage_checked !== undefined ? toBool(vcSrc.coating_damage_checked) : undefined,
    };
    const distortion_alignment = {
      misalignment_ok: vcSrc.misalignment_ok !== undefined ? toBool(vcSrc.misalignment_ok) : undefined,
      no_twist_bow: vcSrc.no_twist_bow !== undefined ? toBool(vcSrc.no_twist_bow) : undefined,
      dimensional_checks_ok: vcSrc.dimensional_checks_ok !== undefined ? toBool(vcSrc.dimensional_checks_ok) : undefined,
    };
    const finish_markings = {
      smooth_transition_tack_ok: vcSrc.smooth_transition_tack_ok !== undefined ? toBool(vcSrc.smooth_transition_tack_ok) : undefined,
      spatter_ok: vcSrc.spatter_ok !== undefined ? toBool(vcSrc.spatter_ok) : undefined,
    };
    out.vc = {
      external_profile_dimensions,
      surface_discontinuities,
      root_penetration,
      cleanliness_contaminants,
      haz_thermal_effects,
      distortion_alignment,
      finish_markings,
      remarks: vcSrc.remarks ?? undefined,
      flw_welder: (() => {
        const s = vcSrc.flw_welder;
        return s === '' || s == null ? undefined : String(s);
      })(),
      fw_welder: (() => {
        const s = vcSrc.fw_welder;
        return s === '' || s == null ? undefined : String(s);
      })(),
      flw_date: (() => {
        const s = vcSrc.flw_date;
        return s === '' || s == null ? undefined : dayjs(String(s));
      })(),
      fw_date: (() => {
        const s = vcSrc.fw_date;
        return s === '' || s == null ? undefined : dayjs(String(s));
      })(),
    };
  }
  return out;
}

export interface VcOverlayStrings {
  flw: string;
  fw: string;
  flwDate: string;
  fwDate: string;
}

/**
 * Persist öncesi payload'a VC welder/tarih overlay'i uygular:
 * dolu değer yazılır; boş değer, alan payload'da hiç yoksa null ile doldurulur
 * (mevcut değer varsa dokunulmaz). Create ve Edit'te birebir aynı olan
 * `merged` IIFE'sinin ortak hali.
 */
export function mergeVcOverlay(base: any, overlay: VcOverlayStrings): any {
  const { flw, fw, flwDate, fwDate } = overlay;
  const p = (base && typeof base === 'object') ? { ...base } : {};
  const vc = (p as any).vc && typeof (p as any).vc === 'object' ? { ...(p as any).vc } : {};
  if (flw) (vc as any).flw_welder = flw; else if ((vc as any).flw_welder === undefined) (vc as any).flw_welder = null;
  if (fw) (vc as any).fw_welder = fw; else if ((vc as any).fw_welder === undefined) (vc as any).fw_welder = null;
  if (flwDate) (vc as any).flw_date = flwDate; else if ((vc as any).flw_date === undefined) (vc as any).flw_date = null;
  if (fwDate) (vc as any).fw_date = fwDate; else if ((vc as any).fw_date === undefined) (vc as any).fw_date = null;
  if (Object.keys(vc).length) (p as any).vc = vc;
  return p;
}

/**
 * Header seviyesindeki heat_no1s/heat_no2s dizilerini payload listesinden
 * türetir (weldingdata_ids sırasıyla hizalı). `p.bc || p` düz-payload
 * fallback'i ve `heat_no_1 ?? heat_no1` legacy anahtar desteği korunur.
 * Base payload seçimi (Edit'in initialPayloadsRef fallback'i) ÇAĞIRANDA kalır.
 */
export function deriveHeatNos(basePayloads: any[]): { heat_no1s: (string | null)[]; heat_no2s: (string | null)[] } {
  const pick = (p: any, k1: string, k2: string) => {
    const src = (p && typeof p === 'object') ? (p.bc || p) : {};
    const v = src?.[k1] ?? src?.[k2] ?? null;
    return v != null && v !== '' ? String(v) : null;
  };
  return {
    heat_no1s: basePayloads.map((p) => pick(p, 'heat_no_1', 'heat_no1')),
    heat_no2s: basePayloads.map((p) => pick(p, 'heat_no_2', 'heat_no2')),
  };
}

/**
 * VC kayıtlarında per-weld welder/tarih payload'da eksikse header
 * dizilerinden (index hizalı) doldurur. `results` yerinde değiştirilir.
 * Davranış Edit'in eski :331-354 bloğundan birebir (try/catch dahil).
 */
export function applyVcHeaderFallback(results: Record<string, any>, record: any): void {
  try {
    if (record?.inspection_type && String(record.inspection_type).toUpperCase() === 'VC') {
      const ids: string[] = Array.isArray(record.weldingdata_ids) ? record.weldingdata_ids.map((x: any) => String(x)) : [];
      const flwA: (string | null)[] = Array.isArray(record.flw_welders) ? record.flw_welders : [];
      const fwA: (string | null)[] = Array.isArray(record.fw_welders) ? record.fw_welders : [];
      const flwD: (string | null)[] = Array.isArray(record.flw_dates) ? record.flw_dates : [];
      const fwD: (string | null)[] = Array.isArray(record.fw_dates) ? record.fw_dates : [];
      ids.forEach((wid, idx) => {
        const cur = results[wid] || {};
        const curVc = cur.vc || {};
        const s1 = curVc.flw_welder;
        const s2 = curVc.fw_welder;
        const v1 = (s1 === undefined || s1 === null || String(s1).trim() === '') ? (flwA[idx] != null ? String(flwA[idx]) : undefined) : s1;
        const v2 = (s2 === undefined || s2 === null || String(s2).trim() === '') ? (fwA[idx] != null ? String(fwA[idx]) : undefined) : s2;
        const d1 = curVc.flw_date;
        const d2 = curVc.fw_date;
        const dv1 = (d1 === undefined || d1 === null || String(d1).trim?.() === '') ? (flwD[idx] ? dayjs(String(flwD[idx])) : undefined) : d1;
        const dv2 = (d2 === undefined || d2 === null || String(d2).trim?.() === '') ? (fwD[idx] ? dayjs(String(fwD[idx])) : undefined) : d2;
        results[wid] = { ...cur, vc: { ...curVc, flw_welder: v1, fw_welder: v2, flw_date: dv1, fw_date: dv2 } };
      });
    }
  } catch {}
}
